import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import DragIndicator from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';

export type QaqcSortableHandleSlot = Readonly<{
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners | undefined;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
}>;

type SortableQaqcShellProps = Readonly<{
  id: string;
  disabled: boolean;
  children: (handleSlot: QaqcSortableHandleSlot) => ReactNode;
}>;

const emptyHandleSlot: QaqcSortableHandleSlot = {
  attributes: {} as DraggableAttributes,
  listeners: undefined,
  setActivatorNodeRef: () => {},
};

export function SortableQaqcShell({ id, disabled, children }: SortableQaqcShellProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const handleSlot: QaqcSortableHandleSlot = disabled
    ? emptyHandleSlot
    : { attributes, listeners, setActivatorNodeRef };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {children(handleSlot)}
    </Box>
  );
}

type SortableDragHandleIconButtonProps = Readonly<{
  handleSlot: QaqcSortableHandleSlot;
  ariaLabel: string;
  sx?: SxProps<Theme>;
}>;

/** Drag handle for sortable rows; stops propagation so AccordionSummary / parents do not steal the gesture. */
export function SortableDragHandleIconButton({
  handleSlot,
  ariaLabel,
  sx,
}: SortableDragHandleIconButtonProps) {
  if (!handleSlot.listeners) {
    return null;
  }

  return (
    <IconButton
      ref={handleSlot.setActivatorNodeRef}
      size="small"
      aria-label={ariaLabel}
      {...handleSlot.attributes}
      {...handleSlot.listeners}
      onPointerDown={(event) => {
        handleSlot.listeners?.onPointerDown?.(event);
        event.stopPropagation();
      }}
      onTouchStart={(event) => {
        handleSlot.listeners?.onTouchStart?.(event);
        event.stopPropagation();
      }}
      onKeyDown={(event) => {
        handleSlot.listeners?.onKeyDown?.(event);
        event.stopPropagation();
      }}
      onClick={(event) => event.stopPropagation()}
      sx={{
        cursor: 'grab',
        flexShrink: 0,
        '&:active': { cursor: 'grabbing' },
        ...sx,
      }}
    >
      <DragIndicator fontSize="small" />
    </IconButton>
  );
}
